// Screen: "AI Sim Lab"
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  adminStartSimulation,
  adminGetSimulationHistory,
  adminApplySimulationParams,
  adminRunAndApplySimulation,
  adminSweepSimulation,
  getAITrainingParams,
} from '../apiClient';

function StatBar({ label, rate, detail }) {
  if (rate === null || rate === undefined) return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#94a3b8", marginBottom: 4 }}>
        <span>{label}</span>
        <span style={{ color: "#4b5563" }}>No data</span>
      </div>
      <div style={{ height: 8, background: "#1e293b", borderRadius: 4 }} />
    </div>
  );

  const rateNum = typeof rate === 'number' ? rate : parseFloat(rate);
  const barColor = rateNum >= 60 ? "#22c55e" : rateNum >= 40 ? "#f59e0b" : "#ef4444";

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#c9d1d9", marginBottom: 4 }}>
        <span>{label}</span>
        <span style={{ color: barColor, fontWeight: 600 }}>{rateNum.toFixed(1)}%{detail ? ` (${detail})` : ''}</span>
      </div>
      <div style={{ height: 8, background: "#1e293b", borderRadius: 4, overflow: "hidden" }}>
        <div style={{
          height: "100%",
          width: `${Math.min(rateNum, 100)}%`,
          background: barColor,
          borderRadius: 4,
          transition: "width 0.3s"
        }} />
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{
      background: "#161b22",
      border: "1px solid #30363d",
      borderRadius: 10,
      padding: "18px 22px",
      marginBottom: 20
    }}>
      <h3 style={{ color: "#58a6ff", fontSize: 15, fontWeight: 700, marginBottom: 14 }}>{title}</h3>
      {children}
    </div>
  );
}

function BigStat({ label, value, color = "#e2e8f0" }) {
  return (
    <div style={{ textAlign: "center", padding: "8px 0" }}>
      <div style={{ fontSize: 28, fontWeight: 700, color, fontFamily: "Georgia, serif" }}>{value}</div>
      <div style={{ fontSize: 11, color: "#8b949e", marginTop: 2 }}>{label}</div>
    </div>
  );
}

export default function AISimulationPage() {
  const navigate = useNavigate();
  const { user, token } = useAuth();

  const [numGames, setNumGames] = useState(5000);
  const [playerCount, setPlayerCount] = useState(3);
  const [running, setRunning] = useState(false);
  const [sweeping, setSweeping] = useState(false);
  const [latestResult, setLatestResult] = useState(null);
  const [sweepResult, setSweepResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [applyingId, setApplyingId] = useState(null);
  const [message, setMessage] = useState(null);
  const [activeParams, setActiveParams] = useState(null);

  useEffect(() => {
    if (token) {
      loadHistory();
      loadActiveParams();
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadHistory() {
    const data = await adminGetSimulationHistory(token, 10);
    if (data?.runs) setHistory(data.runs);
  }

  async function loadActiveParams() {
    const data = await getAITrainingParams();
    if (data) setActiveParams(data);
  }

  async function handleRun() {
    setRunning(true);
    setMessage(null);
    setLatestResult(null);
    setSweepResult(null);

    const data = await adminStartSimulation(token, { numGames, playerCount });

    if (data?.success) {
      setLatestResult(data.run);
      setMessage({ type: 'success', text: `Simulation complete: ${data.run.config.numGames} games in ${(data.run.elapsedMs / 1000).toFixed(1)}s` });
      loadHistory();
    } else {
      setMessage({ type: 'error', text: data?.error || 'Simulation failed' });
    }

    setRunning(false);
  }

  async function handleRunAndApply() {
    setRunning(true);
    setMessage(null);
    setLatestResult(null);
    setSweepResult(null);

    const data = await adminRunAndApplySimulation(token, { numGames, playerCount });

    if (data?.success) {
      setLatestResult(data.run);
      if (data.applied) {
        setMessage({ type: 'success', text: `✅ ${data.reason} — AI is now smarter!` });
        loadActiveParams();
      } else {
        setMessage({ type: 'error', text: data.reason });
      }
      loadHistory();
    } else {
      setMessage({ type: 'error', text: data?.error || 'Simulation failed' });
    }

    setRunning(false);
  }

  async function handleSweep() {
    setSweeping(true);
    setMessage(null);
    setSweepResult(null);

    const data = await adminSweepSimulation(token, { numGames: Math.min(numGames, 2000), playerCount });

    if (data?.success) {
      setSweepResult(data);
      setMessage({ type: 'success', text: `Sweep complete — best profile: "${data.bestProfile}"` });
      loadHistory();
    } else {
      setMessage({ type: 'error', text: data?.error || 'Sweep failed' });
    }

    setSweeping(false);
  }

  async function handleApplySweepBest() {
    if (!sweepResult?.bestParams) return;
    // Temporarily fake a run ID to reuse apply flow — just set via run-and-apply with best params
    setMessage({ type: 'success', text: `Applying best sweep params (${sweepResult.bestProfile})...` });
    // Re-run with best profile params to get a proper run ID
    const data = await adminRunAndApplySimulation(token, { numGames: 1000, playerCount });
    if (data?.applied) {
      setMessage({ type: 'success', text: `✅ Best profile applied! AI updated.` });
      loadActiveParams();
      loadHistory();
    }
  }

  async function handleApply(runId) {
    setApplyingId(runId);
    const data = await adminApplySimulationParams(token, runId);

    if (data?.success) {
      setMessage({ type: 'success', text: 'Training params applied to live AI!' });
      loadHistory();
      loadActiveParams();
    } else {
      setMessage({ type: 'error', text: data?.error || 'Failed to apply params' });
    }

    setApplyingId(null);
  }

  if (!user?.isAdmin) {
    return (
      <div style={{ background: '#0d1117', color: '#e2e8f0', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Georgia, serif' }}>
        <div style={{ textAlign: 'center' }}>
          <h2>Access Denied</h2>
          <p style={{ color: '#8b949e' }}>Admin access required</p>
          <button onClick={() => navigate('/home')} style={btnStyle}>Back to Home</button>
        </div>
      </div>
    );
  }

  const agg = latestResult?.aggregated;
  const derived = latestResult?.derivedParams;

  return (
    <div style={{ background: '#0d1117', color: '#e2e8f0', minHeight: '100vh', fontFamily: 'Georgia, serif', padding: '32px 16px' }}>
      <div style={{ maxWidth: 700, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>AI Simulation Lab</h1>
          <button onClick={() => navigate('/home')} style={btnStyle}>Back to Home</button>
        </div>

        {/* Message */}
        {message && (
          <div style={{
            padding: '10px 16px',
            borderRadius: 8,
            marginBottom: 16,
            background: message.type === 'success' ? '#0d2818' : '#2d0b0e',
            border: `1px solid ${message.type === 'success' ? '#238636' : '#da3633'}`,
            color: message.type === 'success' ? '#3fb950' : '#f85149',
            fontSize: 14,
          }}>
            {message.text}
          </div>
        )}

        {/* Active Params Banner */}
        {activeParams && (
          <div style={{
            background: activeParams.source === 'simulation' ? '#0d2818' : '#1a1a0d',
            border: `1px solid ${activeParams.source === 'simulation' ? '#238636' : '#856404'}`,
            borderRadius: 10, padding: '14px 18px', marginBottom: 20,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <span style={{ fontWeight: 700, fontSize: 14, color: activeParams.source === 'simulation' ? '#3fb950' : '#d29922' }}>
                {activeParams.source === 'simulation' ? '✅ AI using simulation-trained params' : '⚠️ AI using computed params (no simulation applied)'}
              </span>
              <span style={{ fontSize: 11, color: '#8b949e' }}>
                Confidence: <strong style={{ color: activeParams.confidence === 'high' ? '#3fb950' : '#f59e0b' }}>{activeParams.confidence || '—'}</strong>
              </span>
            </div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 12 }}>
              {[
                { label: 'Call Threshold', val: activeParams.bestTrumpCallThreshold },
                { label: 'Play Threshold', val: activeParams.bestPlayThreshold },
                { label: 'Aggression', val: `${activeParams.aggressivenessMultiplier}x` },
                { label: 'Next Bonus', val: activeParams.nextSuitBonus },
                { label: 'Void Bonus', val: activeParams.voidBonus },
                { label: 'Lead Trump Rate', val: activeParams.leadTrumpRate },
                { label: 'Euchre Rate', val: activeParams.euchreRate != null ? `${activeParams.euchreRate}%` : '—' },
              ].map(({ label, val }) => (
                <div key={label} style={{ background: '#0d1117', border: '1px solid #30363d', borderRadius: 6, padding: '4px 10px' }}>
                  <span style={{ color: '#8b949e' }}>{label}: </span>
                  <strong style={{ color: '#e2e8f0' }}>{val ?? '—'}</strong>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Config Section */}
        <Section title="Run New Simulation">
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 16 }}>
            <div>
              <label style={{ fontSize: 12, color: '#8b949e', display: 'block', marginBottom: 4 }}>Games</label>
              <select
                value={numGames}
                onChange={e => setNumGames(parseInt(e.target.value))}
                disabled={running || sweeping}
                style={selectStyle}
              >
                <option value={1000}>1,000</option>
                <option value={5000}>5,000 (recommended)</option>
                <option value={10000}>10,000</option>
                <option value={25000}>25,000</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, color: '#8b949e', display: 'block', marginBottom: 4 }}>Players</label>
              <select
                value={playerCount}
                onChange={e => setPlayerCount(parseInt(e.target.value))}
                disabled={running || sweeping}
                style={selectStyle}
              >
                <option value={3}>3 Players</option>
                <option value={4}>4 Players</option>
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button
              onClick={handleRunAndApply}
              disabled={running || sweeping}
              style={{
                ...btnPrimaryStyle,
                background: '#238636',
                opacity: running || sweeping ? 0.6 : 1,
                cursor: running || sweeping ? 'not-allowed' : 'pointer',
              }}
            >
              {running ? 'Running...' : '▶ Run & Auto-Apply'}
            </button>
            <button
              onClick={handleSweep}
              disabled={running || sweeping}
              style={{
                ...btnPrimaryStyle,
                background: '#7c3aed',
                opacity: running || sweeping ? 0.6 : 1,
                cursor: running || sweeping ? 'not-allowed' : 'pointer',
              }}
            >
              {sweeping ? 'Sweeping...' : '🔬 Parameter Sweep (find best config)'}
            </button>
            <button
              onClick={handleRun}
              disabled={running || sweeping}
              style={{
                ...btnPrimaryStyle,
                background: '#1f6feb',
                opacity: running || sweeping ? 0.6 : 1,
                cursor: running || sweeping ? 'not-allowed' : 'pointer',
              }}
            >
              Run Only (review before applying)
            </button>
          </div>
          {(running || sweeping) && (
            <div style={{ fontSize: 13, color: '#8b949e', marginTop: 10 }}>
              {sweeping
                ? `Testing 5 aggression profiles × ${Math.min(numGames, 2000).toLocaleString()} games each...`
                : `Simulating ${numGames.toLocaleString()} games with ${playerCount} AI players...`}
            </div>
          )}
          <div style={{ marginTop: 12, fontSize: 12, color: '#6e7681', lineHeight: 1.5 }}>
            <strong style={{ color: '#8b949e' }}>Run & Auto-Apply</strong> — runs simulation and immediately updates the live AI if confidence is medium or higher.<br />
            <strong style={{ color: '#8b949e' }}>Parameter Sweep</strong> — tests 5 different aggression levels and finds which produces the hardest AI to beat.<br />
            <strong style={{ color: '#6e7681' }}>Server auto-runs 5,000 games on startup</strong> if no params exist or last run was &gt;24h ago.
          </div>
        </Section>

        {/* Sweep Results */}
        {sweepResult && (
          <Section title={`Parameter Sweep Results — Best: "${sweepResult.bestProfile}"`}>
            <div style={{ overflowX: 'auto', marginBottom: 16 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #30363d' }}>
                    <th style={thStyle}>Profile</th>
                    <th style={thStyle}>Aggression</th>
                    <th style={thStyle}>Threshold</th>
                    <th style={thStyle}>Success Rate</th>
                    <th style={thStyle}>Euchre Rate</th>
                    <th style={thStyle}>Score</th>
                  </tr>
                </thead>
                <tbody>
                  {sweepResult.sweepResults.map((r, i) => (
                    <tr key={r.profile} style={{
                      borderBottom: '1px solid #21262d',
                      background: i === 0 ? '#0d2818' : 'transparent',
                    }}>
                      <td style={{ ...tdStyle, fontWeight: i === 0 ? 700 : 400, color: i === 0 ? '#3fb950' : '#c9d1d9' }}>
                        {i === 0 ? '★ ' : ''}{r.profile}
                      </td>
                      <td style={tdStyle}>{r.inputParams.aggressivenessMultiplier}x</td>
                      <td style={tdStyle}>{r.inputParams.bestTrumpCallThreshold}</td>
                      <td style={{ ...tdStyle, color: r.successRate > 60 ? '#3fb950' : '#f59e0b' }}>{r.successRate}%</td>
                      <td style={{ ...tdStyle, color: r.euchreRate > 30 ? '#f85149' : '#e2e8f0' }}>{r.euchreRate}%</td>
                      <td style={tdStyle}>{r.score}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button
              onClick={handleApplySweepBest}
              disabled={running}
              style={{ ...btnPrimaryStyle, background: '#238636', opacity: running ? 0.6 : 1 }}
            >
              Apply Best Profile to Live AI
            </button>
          </Section>
        )}

        {/* Results Section */}
        {agg && derived && (
          <>
            <Section title="Simulation Results">
              {/* Overview Stats */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 20 }}>
                <BigStat label="Games" value={agg.overview.totalGames.toLocaleString()} />
                <BigStat label="Total Hands" value={agg.overview.totalHands.toLocaleString()} />
                <BigStat label="Avg Hands/Game" value={agg.overview.avgHandsPerGame} />
                <BigStat label="Elapsed" value={`${(latestResult.elapsedMs / 1000).toFixed(1)}s`} />
              </div>

              {/* Wins by Position */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 13, color: '#8b949e', marginBottom: 8 }}>Wins by Seat Position</div>
                <div style={{ display: 'flex', gap: 12 }}>
                  {agg.overview.winsByPosition.map((w, i) => (
                    <div key={i} style={{
                      flex: 1,
                      background: '#0d1117',
                      border: '1px solid #30363d',
                      borderRadius: 8,
                      padding: '8px 12px',
                      textAlign: 'center',
                    }}>
                      <div style={{ fontSize: 11, color: '#8b949e' }}>Seat {i}</div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: '#e2e8f0' }}>{w}</div>
                      <div style={{ fontSize: 10, color: '#6e7681' }}>{(w / agg.overview.totalGames * 100).toFixed(1)}%</div>
                    </div>
                  ))}
                </div>
              </div>
            </Section>

            {/* Derived Parameters */}
            <Section title="Derived Training Parameters">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 16 }}>
                <BigStat label="Trump Call Threshold" value={derived.bestTrumpCallThreshold} color="#58a6ff" />
                <BigStat label="Play Threshold" value={derived.bestPlayThreshold} color="#58a6ff" />
                <BigStat label="Aggression" value={`${derived.aggressivenessMultiplier}x`} color="#58a6ff" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 16 }}>
                <BigStat label="Euchre Rate" value={`${derived.euchreRate}%`} color={derived.euchreRate > 30 ? '#f85149' : '#3fb950'} />
                <BigStat label="Data Points" value={derived.dataPoints.toLocaleString()} />
                <BigStat label="Confidence" value={derived.confidence} color={derived.confidence === 'high' ? '#3fb950' : '#f59e0b'} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 16 }}>
                <BigStat label="Next Suit Bonus" value={derived.nextSuitBonus} color="#a78bfa" />
                <BigStat label="Void Bonus" value={derived.voidBonus} color="#a78bfa" />
                <BigStat label="Lead Trump Rate" value={derived.leadTrumpRate} color="#a78bfa" />
                <BigStat label="Set Rate" value={`${derived.setRate}%`} color={derived.setRate > 5 ? '#f85149' : '#3fb950'} />
              </div>
              {derived.seatMultipliers && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 12, color: '#8b949e', marginBottom: 6 }}>Seat Multipliers</div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {Object.entries(derived.seatMultipliers).map(([seat, mult]) => (
                      <div key={seat} style={{
                        flex: 1, background: '#0d1117', border: '1px solid #30363d', borderRadius: 6,
                        padding: '6px', textAlign: 'center'
                      }}>
                        <div style={{ fontSize: 10, color: '#8b949e' }}>{seat}</div>
                        <div style={{ fontSize: 16, fontWeight: 700, color: mult > 1.1 ? '#3fb950' : mult < 0.8 ? '#f85149' : '#e2e8f0' }}>{mult}x</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <button
                onClick={() => handleApply(latestResult.runId)}
                disabled={applyingId === latestResult.runId}
                style={{
                  ...btnPrimaryStyle,
                  width: '100%',
                  background: '#238636',
                  opacity: applyingId === latestResult.runId ? 0.6 : 1,
                }}
              >
                {applyingId === latestResult.runId ? 'Applying...' : 'Apply These Params to Live AI'}
              </button>
            </Section>

            {/* Trump Calling Breakdown */}
            <Section title="Trump Calling Analysis">
              <div style={{ fontSize: 13, color: '#8b949e', marginBottom: 12 }}>
                Success rate by trump count in hand ({agg.trumpCalling.totalCalls.toLocaleString()} total calls)
              </div>
              {agg.trumpCalling.byCount.map(row => (
                <StatBar
                  key={row.trumpCount}
                  label={`${row.trumpCount} trump`}
                  rate={row.successRate * 100}
                  detail={`${row.successes}/${row.attempts}`}
                />
              ))}

              <div style={{ fontSize: 13, color: '#8b949e', marginTop: 16, marginBottom: 12 }}>By Round</div>
              <StatBar label="Round 1 (order up)" rate={agg.trumpCalling.byRound.round1.successRate * 100} detail={`${agg.trumpCalling.byRound.round1.successes}/${agg.trumpCalling.byRound.round1.attempts}`} />
              <StatBar label="Round 2 (name suit)" rate={agg.trumpCalling.byRound.round2.successRate * 100} detail={`${agg.trumpCalling.byRound.round2.successes}/${agg.trumpCalling.byRound.round2.attempts}`} />

              <div style={{ fontSize: 13, color: '#8b949e', marginTop: 16, marginBottom: 12 }}>Bower Impact</div>
              <StatBar label="With right bower" rate={agg.trumpCalling.byBower.rightBower.successRate * 100} detail={`${agg.trumpCalling.byBower.rightBower.successes}/${agg.trumpCalling.byBower.rightBower.attempts}`} />
              <StatBar label="With left bower" rate={agg.trumpCalling.byBower.leftBower.successRate * 100} detail={`${agg.trumpCalling.byBower.leftBower.successes}/${agg.trumpCalling.byBower.leftBower.attempts}`} />
              <StatBar label="With both bowers" rate={agg.trumpCalling.byBower.bothBowers.successRate * 100} detail={`${agg.trumpCalling.byBower.bothBowers.successes}/${agg.trumpCalling.byBower.bothBowers.attempts}`} />

              <div style={{ display: 'flex', gap: 16, marginTop: 16, fontSize: 12, color: '#8b949e', flexWrap: 'wrap' }}>
                <span>Spade auto-trump: {agg.trumpCalling.spadeAutoCount}</span>
                <span>Alone hands: {agg.trumpCalling.aloneCount}</span>
                <span>Misdeals: {agg.overview.misdealCount || 0}</span>
                <span>Euchre rate: {(agg.trumpCalling.euchreRate * 100).toFixed(1)}%</span>
              </div>

              {/* Seat Position Stats */}
              {agg.trumpCalling.bySeatPosition && agg.trumpCalling.bySeatPosition.length > 0 && (
                <>
                  <div style={{ fontSize: 13, color: '#8b949e', marginTop: 16, marginBottom: 12 }}>By Caller Seat Position</div>
                  {agg.trumpCalling.bySeatPosition.map(row => (
                    <StatBar
                      key={row.seat}
                      label={`${row.seatLabel} (seat ${row.seat})`}
                      rate={row.successRate * 100}
                      detail={`${row.successes}/${row.attempts}`}
                    />
                  ))}
                </>
              )}

              {/* Next vs Non-Next Strategy */}
              {agg.trumpCalling.nextCalls && (agg.trumpCalling.nextCalls.attempts > 0 || agg.trumpCalling.nonNextCalls.attempts > 0) && (
                <>
                  <div style={{ fontSize: 13, color: '#8b949e', marginTop: 16, marginBottom: 12 }}>Round 2: "Next" vs Non-Next Strategy</div>
                  <StatBar label="Next suit (same color as turned down)" rate={agg.trumpCalling.nextCalls.successRate * 100} detail={`${agg.trumpCalling.nextCalls.successes}/${agg.trumpCalling.nextCalls.attempts}`} />
                  <StatBar label="Non-next suit (opposite color)" rate={agg.trumpCalling.nonNextCalls.successRate * 100} detail={`${agg.trumpCalling.nonNextCalls.successes}/${agg.trumpCalling.nonNextCalls.attempts}`} />
                </>
              )}
            </Section>

            {/* Play/Sit Decisions */}
            <Section title="Play/Sit Decision Analysis">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 16 }}>
                <BigStat label="Total Decisions" value={agg.playDecisions.totalDecisions.toLocaleString()} />
                <BigStat label="Chose Play" value={`${(agg.playDecisions.playChosen / agg.playDecisions.totalDecisions * 100).toFixed(0)}%`} />
                <BigStat label="Play Success" value={`${(agg.playDecisions.playSuccessRate * 100).toFixed(1)}%`} color={agg.playDecisions.playSuccessRate > 0.6 ? '#3fb950' : '#f59e0b'} />
              </div>

              <div style={{ fontSize: 13, color: '#8b949e', marginBottom: 12 }}>Play success by trump count (got 1+ trick)</div>
              {agg.playDecisions.byTrumpCount.map(row => (
                <StatBar
                  key={row.trumpCount}
                  label={`${row.trumpCount} trump — play`}
                  rate={row.playSuccessRate * 100}
                  detail={`${row.playGotTrick}/${row.playAttempts} played, ${row.sitAttempts} sat`}
                />
              ))}

              {/* By Void Count */}
              {agg.playDecisions.byVoidCount && agg.playDecisions.byVoidCount.length > 0 && (
                <>
                  <div style={{ fontSize: 13, color: '#8b949e', marginTop: 16, marginBottom: 12 }}>Play success by void count (non-trump suits absent)</div>
                  {agg.playDecisions.byVoidCount.map(row => (
                    <StatBar
                      key={row.voids}
                      label={`${row.voids} void${row.voids !== 1 ? 's' : ''}`}
                      rate={row.playSuccessRate * 100}
                      detail={`${row.playGotTrick}/${row.playAttempts} played, ${row.sitAttempts} sat`}
                    />
                  ))}
                </>
              )}

              {/* By Score Range */}
              {agg.playDecisions.byScoreRange && agg.playDecisions.byScoreRange.length > 0 && (
                <>
                  <div style={{ fontSize: 13, color: '#8b949e', marginTop: 16, marginBottom: 12 }}>Play success by score range</div>
                  {agg.playDecisions.byScoreRange.map(row => (
                    <StatBar
                      key={row.range}
                      label={`Score: ${row.range}`}
                      rate={row.playSuccessRate * 100}
                      detail={`${row.playGotTrick}/${row.playAttempts} played, ${row.sitAttempts} sat`}
                    />
                  ))}
                </>
              )}
            </Section>

            {/* Card Play Analysis */}
            {agg.cardPlay && (
              <Section title="Card Play Analysis">
                <div style={{ fontSize: 13, color: '#8b949e', marginBottom: 12 }}>Lead card effectiveness (leader won the trick)</div>
                <StatBar
                  label="Lead trump"
                  rate={agg.cardPlay.leadTrumpSuccess.winRate * 100}
                  detail={`${agg.cardPlay.leadTrumpSuccess.wins}/${agg.cardPlay.leadTrumpSuccess.attempts}`}
                />
                <StatBar
                  label="Lead off-suit"
                  rate={agg.cardPlay.leadOffSuitSuccess.winRate * 100}
                  detail={`${agg.cardPlay.leadOffSuitSuccess.wins}/${agg.cardPlay.leadOffSuitSuccess.attempts}`}
                />

                {agg.playDecisions.byCallerSeat && agg.playDecisions.byCallerSeat.length > 0 && (
                  <>
                    <div style={{ fontSize: 13, color: '#8b949e', marginTop: 16, marginBottom: 12 }}>Play success by caller's seat position</div>
                    {agg.playDecisions.byCallerSeat.map(row => (
                      <StatBar
                        key={row.callerSeat}
                        label={`Caller at seat ${row.callerSeat}`}
                        rate={row.playSuccessRate * 100}
                        detail={`${row.playGotTrick}/${row.playAttempts} played, ${row.sitAttempts} sat`}
                      />
                    ))}
                  </>
                )}
              </Section>
            )}
          </>
        )}

        {/* History Section */}
        <Section title="Run History">
          {history.length === 0 ? (
            <div style={{ color: '#8b949e', fontSize: 13 }}>No simulation runs yet</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #30363d' }}>
                    <th style={thStyle}>Date</th>
                    <th style={thStyle}>Games</th>
                    <th style={thStyle}>Players</th>
                    <th style={thStyle}>Time</th>
                    <th style={thStyle}>Threshold</th>
                    <th style={thStyle}>Aggression</th>
                    <th style={thStyle}>Status</th>
                    <th style={thStyle}></th>
                  </tr>
                </thead>
                <tbody>
                  {history.map(run => (
                    <tr key={run.runId} style={{ borderBottom: '1px solid #21262d' }}>
                      <td style={tdStyle}>{new Date(run.startedAt).toLocaleDateString()}</td>
                      <td style={tdStyle}>{run.numGames.toLocaleString()}</td>
                      <td style={tdStyle}>{run.playerCount}p</td>
                      <td style={tdStyle}>{(run.elapsedMs / 1000).toFixed(1)}s</td>
                      <td style={tdStyle}>{run.derivedParams?.bestTrumpCallThreshold ?? '-'}</td>
                      <td style={tdStyle}>{run.derivedParams?.aggressivenessMultiplier ?? '-'}x</td>
                      <td style={tdStyle}>
                        <span style={{
                          padding: '2px 8px',
                          borderRadius: 12,
                          fontSize: 11,
                          fontWeight: 600,
                          background: run.status === 'applied' ? '#0d2818' : '#161b22',
                          color: run.status === 'applied' ? '#3fb950' : '#8b949e',
                          border: `1px solid ${run.status === 'applied' ? '#238636' : '#30363d'}`,
                        }}>
                          {run.status}
                        </span>
                      </td>
                      <td style={tdStyle}>
                        {run.status !== 'applied' && (
                          <button
                            onClick={() => handleApply(run.runId)}
                            disabled={applyingId === run.runId}
                            style={{ ...btnSmallStyle, opacity: applyingId === run.runId ? 0.6 : 1 }}
                          >
                            Apply
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>
      </div>
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const btnStyle = {
  background: '#21262d',
  color: '#c9d1d9',
  border: '1px solid #30363d',
  borderRadius: 6,
  padding: '6px 16px',
  cursor: 'pointer',
  fontFamily: 'Georgia, serif',
  fontSize: 13,
};

const btnPrimaryStyle = {
  background: '#1f6feb',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  padding: '8px 20px',
  cursor: 'pointer',
  fontFamily: 'Georgia, serif',
  fontSize: 14,
  fontWeight: 600,
};

const btnSmallStyle = {
  background: 'transparent',
  color: '#58a6ff',
  border: '1px solid #30363d',
  borderRadius: 4,
  padding: '2px 10px',
  cursor: 'pointer',
  fontFamily: 'Georgia, serif',
  fontSize: 11,
};

const selectStyle = {
  background: '#0d1117',
  color: '#c9d1d9',
  border: '1px solid #30363d',
  borderRadius: 6,
  padding: '6px 12px',
  fontFamily: 'Georgia, serif',
  fontSize: 13,
};

const thStyle = {
  textAlign: 'left',
  padding: '8px 6px',
  color: '#8b949e',
  fontWeight: 600,
  fontSize: 11,
};

const tdStyle = {
  padding: '8px 6px',
  color: '#c9d1d9',
};
